import { useMemo, useState } from "react";

/**
 * Generic hook for filtering by a key in an array of objects.
 * @param items Array of objects to filter
 * @param key Key to filter by (must exist on all items)
 */
export function useFilter<T, K extends keyof T>(
  items: T[] | undefined,
  key: K,
) {
  const [selected, setSelected] = useState<T[K] | null>(null);

  const options: T[K][] = useMemo(() => {
    if (!items) return [];
    return Array.from(
      new Set(
        items.map((item) => item[key]).filter((v): v is T[K] => v != null),
      ),
    );
  }, [items, key]);

  return {
    selected,
    setSelected,
    options,
  };
}

/**
 * Generic utility to filter an array of objects by a set of filters.
 * Each filter key is compared for strict equality if the value is not null/undefined.
 * The filter object is strictly typed: keys are keyof T, values are T[K] | null.
 */
export function applyFilters<
  T,
  F extends Partial<{ [K in keyof T]: T[K] | null }>,
>(items: T[], filters: F): T[] {
  return items.filter((item) =>
    (Object.entries(filters) as [keyof T, T[keyof T] | null][]).every(
      ([key, value]) => value == null || item[key] === value,
    ),
  );
}

export function groupByKey<T, K extends keyof T>(
  items: T[],
  key: K,
): Record<string, T[]> {
  return items.reduce(
    (groups, item) => {
      const groupKey = String(item[key] ?? "undefined");
      if (!(groupKey in groups)) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>,
  );
}
